// Schedule page — the cross-contract schedule view. Two switchable
// paradigms share one page: the Calendar grid and the Density heatmap
// + 3-day agenda (the ViewToggle in the header swaps the body; filters,
// stats, legend and the detail drawer are identical between them).
// The header follows the ContractRegister inline convention (no
// PageHeader component): text-h1 title + lede + an action row. Export
// is a disabled "Coming soon" stub; Today resets the period to the
// current month. URL state drives the GET /manage/schedule query and
// the open drawer (?activity=); clicking a chip or agenda row opens
// the no-backdrop ActivityDrawer (Phase 2) — the drawer's "Open in
// contract →" is the path to the underlying contract page. Browser
// back closes the drawer (activity uses push history).
//
// Status-bucket filtering is CLIENT-SIDE. The backend's severity-status
// vocabulary is the per-activity colour, not a bucket — sending a
// bucket slug as ?status= produced a 400 that wiped the calendar. The
// shared scheduleBuckets module owns the bucket type, label, type
// guard, predicate and filter helper; StatsRow and FilterStrip read
// from the same source. A stale or pasted URL value is normalised to
// undefined at the page boundary so no garbage bucket can reach
// downstream consumers.
//
// The density view fetches the SAME calendar surface the heatmap
// renders. Both halves use getDensitySpan(anchor, range) so the API
// {from,to} cannot drift from the visible grid.

import { useMemo } from "react";
import { Calendar as CalendarIcon, Crosshair, Download } from "lucide-react";
import { LoadingSpinner, Alert } from "../../components/common";
import { useScheduleUrlState } from "../../hooks/useScheduleUrlState";
import { useSchedule } from "../../hooks/useSchedule";
import { useActivityDetail } from "../../hooks/useActivityDetail";
import { useManagedContracts } from "../../hooks/useManagedContracts";
import FilterStrip from "../../components/manage/schedule/FilterStrip";
import StatsRow, {
  type StatsCardKey,
} from "../../components/manage/schedule/StatsRow";
import ScheduleLegend from "../../components/manage/schedule/ScheduleLegend";
import CalendarToolbar from "../../components/manage/schedule/CalendarToolbar";
import CalendarGrid from "../../components/manage/schedule/CalendarGrid";
import ViewToggle from "../../components/manage/schedule/ViewToggle";
import DensityToolbar from "../../components/manage/schedule/DensityToolbar";
import DensityHeatmap from "../../components/manage/schedule/DensityHeatmap";
import DensityAgenda from "../../components/manage/schedule/DensityAgenda";
import ActivityDrawer from "../../components/manage/schedule/ActivityDrawer";
import type { Activity, ActivityKind } from "../../types/schedule";
import {
  filterByBucket,
  isScheduleStatusBucket,
  type ScheduleStatusBucket,
} from "../../components/manage/schedule/scheduleBuckets";
import { SCHEDULE_KINDS } from "../../components/manage/schedule/kindVocabulary";
import {
  getDensitySpan,
  type DensityRange,
} from "../../utils/schedule";

// YYYY-MM for the given (or current) date.
function monthKey(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

// First and last calendar day of the YYYY-MM period (inclusive).
function monthBounds(period: string): { from: string; to: string } {
  const [y, m] = period.split("-").map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const last = new Date(Date.UTC(y, m, 0));
  const iso = (d: Date) =>
    `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(
      d.getUTCDate(),
    ).padStart(2, "0")}`;
  return { from: iso(first), to: iso(last) };
}

function shiftMonth(period: string, delta: number): string {
  const [y, m] = period.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return monthKey(d);
}

function periodLabel(period: string): string {
  const [y, m] = period.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-AU", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

const RANGE_MONTHS: Record<DensityRange, number> = {
  quarter: 3,
  "6m": 6,
  "12m": 12,
};

function isValidRange(v: string | undefined): v is DensityRange {
  return v === "quarter" || v === "6m" || v === "12m";
}

// Density spans backward from "now" so the strategic view shows the
// run-up to today plus the immediate horizon. The anchor is the first
// of the month RANGE_MONTHS-1 before the current period month.
function densityAnchor(period: string, range: DensityRange): string {
  const [y, m] = period.split("-").map(Number);
  const back = RANGE_MONTHS[range] - 1;
  const d = new Date(Date.UTC(y, m - 1 - back, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-01`;
}

function densityRangeLabel(anchor: string, range: DensityRange): string {
  const [ay, am] = anchor.split("-").map(Number);
  const start = new Date(Date.UTC(ay, am - 1, 1));
  const end = new Date(Date.UTC(ay, am - 1 + RANGE_MONTHS[range] - 1, 1));
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-AU", {
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
  return `${fmt(start)} → ${fmt(end)}`;
}

// Map StatsRow's internal card key to the canonical URL bucket slug.
const STATUS_BUCKET: Record<StatsCardKey, ScheduleStatusBucket> = {
  overdue: "overdue",
  dueThisWeek: "due-soon",
  pendingDecisions: "pending-decisions",
};
const ACTIVE_KEY_FROM_BUCKET: Record<ScheduleStatusBucket, StatsCardKey> = {
  overdue: "overdue",
  "due-soon": "dueThisWeek",
  "pending-decisions": "pendingDecisions",
};

export default function Schedule() {
  const {
    state,
    setView,
    setPeriod,
    setRange,
    setZoom,
    setFocus,
    setContracts,
    setHide,
    setStatus,
    setQ,
    setActivity,
    resetFilters,
  } = useScheduleUrlState();

  // Header — derive everything the API params block needs BEFORE the
  // fetch so the density-view {from,to} can use getDensitySpan and the
  // bucket predicate can use today.
  const period = state.period ?? monthKey(new Date());
  const range: DensityRange = isValidRange(state.range) ? state.range : "6m";
  const anchor = densityAnchor(period, range);
  // `today` is memoised so the filter / fetch derivations downstream
  // have a stable Date reference per render (a bare `new Date()` would
  // re-fire useMemo every render and re-run the bucket filter).
  const today = useMemo(() => new Date(), []);
  const todayIso = `${today.getUTCFullYear()}-${String(
    today.getUTCMonth() + 1,
  ).padStart(2, "0")}-${String(today.getUTCDate()).padStart(2, "0")}`;

  // Type-guard the URL ?status= so any historical or pasted slug
  // (`due-this-week`, `pending-decision`, garbage…) collapses to
  // "no bucket active" rather than reaching FilterStrip / filterByBucket.
  const activeBucket: ScheduleStatusBucket | undefined =
    isScheduleStatusBucket(state.status) ? state.status : undefined;

  // The endpoint takes `kinds` as an include-list (omit = all). The URL
  // carries the HIDDEN set, so only send `kinds` when something is
  // hidden, as the complement of `hide`.
  const visibleKinds = (SCHEDULE_KINDS as readonly ActivityKind[]).filter(
    (k) => !state.hide.includes(k),
  );
  const kindsParam =
    state.hide.length > 0 ? visibleKinds.join(",") : undefined;

  // Per-view {from,to}: the calendar fetches the visible month; density
  // fetches the entire week-grid surface the heatmap renders.
  const { from, to } = useMemo(
    () =>
      state.view === "density"
        ? getDensitySpan(anchor, range)
        : monthBounds(period),
    [state.view, period, anchor, range],
  );

  const { data, isLoading, error } = useSchedule({
    from,
    to,
    contracts: state.contracts?.join(","),
    kinds: kindsParam,
    search: state.q,
  });

  const { data: contractsData } = useManagedContracts();
  const contractOptions = (contractsData?.items ?? []).map((c) => ({
    id: c.id,
    name: c.title,
    // Sparse stored rows can omit contractNumber; FilterStrip searches and
    // renders ref, so an empty string keeps both safe.
    ref: c.contractNumber ?? "",
  }));

  // Stable reference for the activities list — `data?.activities ?? []`
  // would mint a new [] every render when data is undefined, busting
  // the bucket-filter memo.
  const activities: Activity[] = useMemo(
    () => data?.activities ?? [],
    [data],
  );
  const totalInRange = data?.totalInRange ?? 0;

  // Bucket filter runs client-side; the calendar/heatmap/agenda/drawer
  // and the empty-state all key off the FILTERED list. StatsRow stays
  // on the unfiltered list so the tile counts represent the full
  // range (same contract as CGN-31's SummaryStrip).
  const filteredActivities = useMemo(
    () => filterByBucket(activities, activeBucket, today),
    [activities, activeBucket, today],
  );

  const handleToggleKind = (kind: ActivityKind) => {
    const next = state.hide.includes(kind)
      ? state.hide.filter((k) => k !== kind)
      : [...state.hide, kind];
    setHide(next);
  };

  // Phase 2 — a chip / agenda row opens the detail drawer (sets
  // ?activity=, push history so browser-back closes it). The drawer's
  // "Open in contract →" is the path to the contract page.
  const handleActivityOpen = (activity: Activity) => {
    setActivity(activity.id);
  };

  // Click-to-filter — StatsRow emits a component-internal camelCase
  // card key; the page is the mapper that translates to the URL bucket.
  // Toggle: clicking the active card clears the filter.
  const handleStatCardClick = (key: StatsCardKey) => {
    const bucket = STATUS_BUCKET[key];
    setStatus(activeBucket === bucket ? undefined : bucket);
  };
  const activeStatKey: StatsCardKey | null = activeBucket
    ? ACTIVE_KEY_FROM_BUCKET[activeBucket]
    : null;

  const selectedDate = state.focus ?? todayIso;

  // The drawer fetches detail for ?activity=; prev/next walk the
  // currently-filtered, ordered activity set (the aggregator's order).
  const { data: activityDetail, isLoading: detailLoading } =
    useActivityDetail(state.activity);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-h1 text-slate-900 dark:text-slate-50">
            Schedule
          </h1>
          <p className="mt-1 max-w-3xl text-slate-600 dark:text-slate-400">
            Every upcoming insurance renewal, deliverable, milestone,
            meeting, KPI period and variation across your contracts — in
            one place.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled
            title="Coming soon"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-body-sm font-semibold text-slate-400 disabled:cursor-not-allowed dark:border-slate-600 dark:text-slate-500"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Export
          </button>
          <button
            type="button"
            onClick={() => {
              setPeriod(monthKey(new Date()));
              setFocus(undefined);
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-caption font-medium text-slate-600 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <Crosshair className="h-3.5 w-3.5" aria-hidden="true" /> Today
          </button>
          <ViewToggle value={state.view} onChange={setView} />
        </div>
      </div>

      <FilterStrip
        contractOptions={contractOptions}
        selectedContracts={state.contracts}
        hiddenKinds={state.hide}
        status={activeBucket}
        search={state.q ?? ""}
        onContractsChange={setContracts}
        onToggleKind={handleToggleKind}
        onStatusChange={setStatus}
        onSearchChange={(v) => setQ(v === "" ? undefined : v)}
        onReset={resetFilters}
      />

      <StatsRow
        activities={activities}
        totalInRange={totalInRange}
        today={today}
        periodLabel={periodLabel(period)}
        onCardClick={handleStatCardClick}
        activeKey={activeStatKey}
      />

      <ScheduleLegend />

      {state.view === "density" ? (
        <DensityToolbar
          rangeLabel={densityRangeLabel(anchor, range)}
          range={range}
          onRangeChange={setRange}
        />
      ) : (
        <CalendarToolbar
          periodLabel={periodLabel(period)}
          counts={{
            overdue: filteredActivities.filter((a) => a.status === "red").length,
            dueThisWeek: filteredActivities.filter((a) => {
              if (a.status !== "brand") return false;
              const todayMs = Date.UTC(
                today.getUTCFullYear(),
                today.getUTCMonth(),
                today.getUTCDate(),
              );
              const d = new Date(`${a.date}T00:00:00Z`);
              if (Number.isNaN(d.getTime())) return false;
              const dm = Date.UTC(
                d.getUTCFullYear(),
                d.getUTCMonth(),
                d.getUTCDate(),
              );
              return dm >= todayMs && dm <= todayMs + 7 * 86_400_000;
            }).length,
            inView: totalInRange,
          }}
          zoom={state.zoom}
          onZoomChange={setZoom}
          onPrev={() => setPeriod(shiftMonth(period, -1))}
          onNext={() => setPeriod(shiftMonth(period, 1))}
        />
      )}

      {error && (
        <Alert variant="error">{(error as Error).message}</Alert>
      )}

      {isLoading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="md" />
        </div>
      )}

      {!isLoading && !error && filteredActivities.length === 0 && (
        <div className="card flex flex-col items-center justify-center py-16 text-center">
          <CalendarIcon className="mb-4 h-12 w-12 text-slate-300 dark:text-slate-600" />
          <p className="text-body font-medium text-slate-700 dark:text-slate-200">
            {activeBucket && activities.length > 0
              ? "No activities match this filter"
              : "No activities match"}
          </p>
          <p className="mt-1 max-w-md text-body-sm text-slate-500 dark:text-slate-400">
            Try clearing the status filter, adding more contracts back,
            or extending the date range.
          </p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-4 rounded-md px-4 py-2 text-body-sm font-semibold text-brand-600 hover:bg-brand-50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-brand-400 dark:hover:bg-brand-950"
          >
            Clear filters
          </button>
        </div>
      )}

      {!isLoading && !error && filteredActivities.length > 0 && (
        state.view === "density" ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_420px]">
            <DensityHeatmap
              range={range}
              anchor={anchor}
              activities={filteredActivities}
              selectedDate={selectedDate}
              today={today}
              onSelectDate={setFocus}
            />
            <DensityAgenda
              selectedDate={selectedDate}
              activities={filteredActivities}
              today={today}
              onOpen={handleActivityOpen}
              onShowAllForDate={(iso) => {
                // The density agenda's '+N more' on a Next-upcoming
                // date opens the drawer at the first activity on that
                // date. The drawer's prev/next walks the whole list,
                // so the user can scan everything on that date from
                // there. Lifting a shared DayPopover up to this page
                // is a larger refactor and out of scope for this PR.
                const first = filteredActivities.find((a) => a.date === iso);
                if (first) setActivity(first.id);
              }}
            />
          </div>
        ) : (
          <CalendarGrid
            period={
              state.zoom === "month" ? period : selectedDate
            }
            zoom={state.zoom}
            activities={filteredActivities}
            onActivityOpen={handleActivityOpen}
            today={today}
          />
        )
      )}

      {state.activity && (
        <ActivityDrawer
          activityId={state.activity}
          activityList={filteredActivities}
          detail={activityDetail}
          isLoading={detailLoading}
          onClose={() => setActivity(undefined)}
          onNavigate={(id) => setActivity(id)}
        />
      )}
    </div>
  );
}
